export const M5_UNIT_6 = {
  id: "m5-u6-entertainment",
  title: "Unit 6: Entertainment & Media",
  topic: "Talking about movies, series, books, and performances",
  iconType: "film",
  grammarImage: "/grammar/grammar-m5-u6.jpg",
  grammarText: "This unit teaches vocabulary to discuss movies, TV shows, books, plays, and other entertainment. You'll learn words for genres (comedy, thriller, horror), people (actor, director, author, villain, hero), story elements (plot, twist, sequel, spoiler), and opinions (boring, exciting, amazing, terrible). We combine this vocabulary with grammar you already know: past simple (I watched, loved), past to be (it was good, they were bad), and could/couldn't (I couldn't stop reading). This helps you share recommendations, write reviews, and have natural conversations about what you watch and read.",

  sets: [
    // ===================================================================
    // SET 1: DISCOVERING GENRES (10 questions) - A2
    // ===================================================================
    {
      id: "m5-u6-s1",
      title: "Set 1: Types of Entertainment",
      questions: [
        {
          type: "match",
          q: "Match the genres to their descriptions:",
          pairs: [
            { left: "Comedy", right: "Funny, makes you laugh" },
            { left: "Horror", right: "Scary, frightening" },
            { left: "Romance", right: "Love story" }
          ],
          a: "completed",
          successtip: "✓ Three basic genres! Comedy 😂, Horror 😱, Romance 💕"
        },
        {
          type: "mc",
          q: "Last night I watched a ___ movie. It was so scary I couldn't sleep!",
          options: ["horror", "comedy", "romance"],
          a: "horror",
          successtip: "✓ HORROR! Scary movies = sleepless nights! 😱🛌"
        },
        {
          type: "input",
          q: "I love ___ movies because they make me laugh. (genre)",
          a: "comedy",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["comedies"] },
          successtip: "✓ COMEDY! Laughter = best medicine! 😂"
        },
        {
          type: "match",
          q: "Match more genres:",
          pairs: [
            { left: "Action", right: "Explosions, fights, car chases" },
            { left: "Thriller", right: "Suspense, mystery, tension" },
            { left: "Drama", right: "Serious, emotional stories" }
          ],
          a: "completed",
          successtip: "✓ Action 💥, Thriller 😰, Drama 😢! More variety!"
        },
        {
          type: "order",
          q: "Traduce: Ayer vi una película de acción y fue increíble.",
          words: ["Yesterday", "I", "watched", "an", "action", "movie", "and", "it", "was", "amazing"],
          a: "Yesterday I watched an action movie and it was amazing",
          successtip: "✓ Past simple + genre + opinion! Perfect combo! 💥"
        },
        {
          type: "mc",
          q: "She watched a ___ last week. It was about a couple falling in love in Paris.",
          options: ["romance", "horror", "action"],
          a: "romance",
          successtip: "✓ ROMANCE! Paris = city of love! 💕🗼"
        },
        {
          type: "input",
          q: "MINI-STORY: 'I started watching a ___ on Netflix. There was a murder and the detective tried to solve it.' (genre)",
          a: "thriller",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["mystery", "crime"] },
          successtip: "✓ THRILLER! (Mystery/crime also work!) Whodunit stories! 🔍"
        },
        {
          type: "match",
          q: "Mood → Genre:",
          pairs: [
            { left: "Want to laugh?", right: "Watch a comedy" },
            { left: "Want adrenaline?", right: "Watch an action movie" },
            { left: "Want to cry?", right: "Watch a drama" }
          ],
          a: "completed",
          successtip: "✓ Choose genre by mood! 😂💥😢"
        },
        {
          type: "mc",
          q: "CONTEXT: 'The movie had zombies, ghosts, and jump scares. I was terrified!' This is a ___.",
          options: ["horror movie", "romantic comedy", "documentary"],
          a: "horror movie",
          successtip: "✓ HORROR! Zombies + ghosts + jump scares = classic horror! 🧟‍♂️👻"
        },
        {
          type: "order",
          q: "Traduce: Las películas de comedia me hacen sentir mejor cuando estoy triste.",
          words: ["Comedy", "movies", "make", "me", "feel", "better", "when", "I'm", "sad"],
          a: "Comedy movies make me feel better when I'm sad",
          successtip: "✓ Comfort viewing! Comedy = therapy! 😂❤️"
        }
      ]
    },

    // ===================================================================
    // SET 2: CHARACTERS & PEOPLE (10 questions) - A2
    // ===================================================================
    {
      id: "m5-u6-s2",
      title: "Set 2: Who's Who in Entertainment",
      questions: [
        {
          type: "match",
          q: "Match the roles to their jobs:",
          pairs: [
            { left: "Actor", right: "Performs in movies/TV" },
            { left: "Director", right: "Controls the movie/show" },
            { left: "Author", right: "Writes books" }
          ],
          a: "completed",
          successtip: "✓ Behind and in front of camera! 🎬📚"
        },
        {
          type: "mc",
          q: "The ___ of the movie was incredible! She won an Oscar for her performance.",
          options: ["actress", "director", "author"],
          a: "actress",
          successtip: "✓ ACTRESS! Lead performer! 🏆✨"
        },
        {
          type: "input",
          q: "Christopher Nolan is my favorite ___. He directed Inception and Interstellar.",
          a: "director",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ DIRECTOR! Nolan = genius filmmaker! 🎥🧠"
        },
        {
          type: "match",
          q: "Match character types:",
          pairs: [
            { left: "Hero", right: "Good guy, saves the day" },
            { left: "Villain", right: "Bad guy, causes problems" },
            { left: "Main character", right: "Story centers on them" }
          ],
          a: "completed",
          successtip: "✓ Hero 🦸, Villain 🦹, Main character 🌟!"
        },
        {
          type: "order",
          q: "Traduce: El villano de esa película era aterrador pero fascinante.",
          words: ["The", "villain", "in", "that", "movie", "was", "terrifying", "but", "fascinating"],
          a: "The villain in that movie was terrifying but fascinating",
          successtip: "✓ Great villains = memorable! Heath Ledger's Joker vibes! 🃏"
        },
        {
          type: "mc",
          q: "MINI-STORY: 'Batman is the ___ who saves Gotham City. The Joker is the ___ who tries to destroy it.'",
          options: ["hero / villain", "villain / hero", "actor / director"],
          a: "hero / villain",
          successtip: "✓ HERO/VILLAIN! Classic good vs evil! 🦇🃏"
        },
        {
          type: "input",
          q: "J.K. Rowling is the ___ of the Harry Potter books.",
          a: "author",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["writer"] },
          successtip: "✓ AUTHOR! (Writer also works!) She created the wizarding world! ⚡📚"
        },
        {
          type: "match",
          q: "Famous people:",
          pairs: [
            { left: "Movie star", right: "Famous actor/actress" },
            { left: "Lead actor", right: "Main performer" },
            { left: "Supporting actor", right: "Secondary performer" }
          ],
          a: "completed",
          successtip: "✓ Different levels of fame and roles! 🌟"
        },
        {
          type: "mc",
          q: "DIALOGUE: A: 'Who's your favorite actor?' B: 'I love Ryan Gosling! He was amazing in La La Land.' This talks about:",
          options: ["An actor and his performance", "A director", "A book"],
          a: "An actor and his performance",
          successtip: "✓ Discussing actors = common conversation! 🎭"
        },
        {
          type: "order",
          q: "Traduce: El actor principal ganó un premio, pero el villano fue más memorable.",
          words: ["The", "lead", "actor", "won", "an", "award,", "but", "the", "villain", "was", "more", "memorable"],
          a: "The lead actor won an award, but the villain was more memorable",
          successtip: "✓ Sometimes villains steal the show! 🏆🦹"
        }
      ]
    },

    // ===================================================================
    // SET 3: OPINIONS & REACTIONS (10 questions) - A2+
    // ===================================================================
    {
      id: "m5-u6-s3",
      title: "Set 3: What Did You Think?",
      questions: [
        {
          type: "match",
          q: "Match opinions to meanings:",
          pairs: [
            { left: "Amazing", right: "Extremely good" },
            { left: "Boring", right: "Not interesting, dull" },
            { left: "Terrible", right: "Very bad" }
          ],
          a: "completed",
          successtip: "✓ Strong opinions! Amazing 😍, Boring 😴, Terrible 👎"
        },
        {
          type: "mc",
          q: "COMPLETE: 'I watched that new series last night. It was so ___! I fell asleep after 10 minutes.'",
          options: ["boring", "exciting", "amazing"],
          a: "boring",
          successtip: "✓ BORING! Fell asleep = ultimate sign of boredom! 😴"
        },
        {
          type: "input",
          q: "MINI-REVIEW: 'The movie was ___! I loved every minute. I couldn't stop smiling!' (positive adjective)",
          a: "amazing",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["incredible", "awesome", "fantastic", "great", "wonderful"] },
          successtip: "✓ Many positive words work! Amazing/incredible/awesome! 😍"
        },
        {
          type: "order",
          q: "Traduce: La serie fue tan emocionante que no pude dejar de verla.",
          words: ["The", "series", "was", "so", "exciting", "that", "I", "couldn't", "stop", "watching"],
          a: "The series was so exciting that I couldn't stop watching",
          successtip: "✓ 'So...that' = cause and effect! Binge-worthy! 📺"
        },
        {
          type: "mc",
          q: "DIALOGUE: A: 'How was the movie?' B: 'Honestly? It was ___. I wanted my money back!' What's B's opinion?",
          options: ["terrible", "amazing", "okay"],
          a: "terrible",
          successtip: "✓ TERRIBLE! Wanted money back = worst review! 💸👎"
        },
        {
          type: "match",
          q: "Match reactions:",
          pairs: [
            { left: "I loved it!", right: "Very positive" },
            { left: "It was okay", right: "Neutral/meh" },
            { left: "I hated it!", right: "Very negative" }
          ],
          a: "completed",
          successtip: "✓ Spectrum from love to hate! ❤️😐💔"
        },
        {
          type: "input",
          q: "STORY: 'The book started well, but the ending was ___. I was so disappointed!' (negative)",
          a: "disappointing",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["bad", "terrible", "awful", "horrible"] },
          successtip: "✓ Bad endings ruin everything! 📖😞"
        },
        {
          type: "mc",
          q: "Which is MORE positive? A) 'It was good' B) 'It was incredible!'",
          options: ["B is more positive", "A is more positive", "Same level"],
          a: "B is more positive",
          successtip: "✓ Incredible > good! Use strong adjectives for strong feelings! 🌟"
        },
        {
          type: "order",
          q: "Traduce: Los efectos especiales fueron impresionantes, pero la trama fue confusa.",
          words: ["The", "special", "effects", "were", "impressive,", "but", "the", "plot", "was", "confusing"],
          a: "The special effects were impressive, but the plot was confusing",
          successtip: "✓ Mixed review! Great visuals, weak story! Common combo! 💥🤔"
        },
        {
          type: "mc",
          q: "REAL REVIEW: 'I watched it with low expectations and was pleasantly surprised! It was much better than I thought. Highly recommend!' This person:",
          options: ["Liked it more than expected", "Hated it", "Expected it to be good"],
          a: "Liked it more than expected",
          successtip: "✓ Positive surprise! Low expectations → exceeded them! ⬇️➡️⬆️"
        }
      ]
    },

    // ===================================================================
    // SET 4: PLOT & STORY ELEMENTS (10 questions) - A2+
    // ===================================================================
    {
      id: "m5-u6-s4",
      title: "Set 4: The Story",
      questions: [
        {
          type: "match",
          q: "Match story vocabulary:",
          pairs: [
            { left: "Plot", right: "The story/what happens" },
            { left: "Twist", right: "Unexpected surprise" },
            { left: "Ending", right: "How the story finishes" }
          ],
          a: "completed",
          successtip: "✓ Plot = story, Twist = surprise, Ending = conclusion! 📖"
        },
        {
          type: "mc",
          q: "COMPLETE: 'The ___ was confusing. I didn't understand what was happening in the movie.'",
          options: ["plot", "actor", "genre"],
          a: "plot",
          successtip: "✓ PLOT! When story is confusing = frustrating! 🤔"
        },
        {
          type: "input",
          q: "REACTION: 'I didn't expect that ___! The villain was actually the hero's brother!' (surprise element)",
          a: "twist",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["plot twist", "surprise"] },
          successtip: "✓ TWIST! Mind = blown! 🤯"
        },
        {
          type: "order",
          q: "Traduce: El final de la película me sorprendió mucho.",
          words: ["The", "ending", "of", "the", "movie", "surprised", "me", "a", "lot"],
          a: "The ending of the movie surprised me a lot",
          successtip: "✓ Good endings = memorable! 🎬✨"
        },
        {
          type: "match",
          q: "More story elements:",
          pairs: [
            { left: "Setting", right: "Where/when story happens" },
            { left: "Conflict", right: "The main problem" },
            { left: "Cliffhanger", right: "Unresolved ending" }
          ],
          a: "completed",
          successtip: "✓ Setting 🏙️, Conflict ⚡, Cliffhanger 😱!"
        },
        {
          type: "mc",
          q: "DIALOGUE: A: 'Did you finish the season?' B: 'Yes! But it ended on a huge ___! Now I have to wait a year for answers!' What happened?",
          options: ["cliffhanger", "ending", "twist"],
          a: "cliffhanger",
          successtip: "✓ CLIFFHANGER! Unresolved ending = torture! Have to wait! 😤⏰"
        },
        {
          type: "input",
          q: "MINI-REVIEW: 'The ___ is set in the future. Robots have taken over the world.' (story location/time)",
          a: "setting",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["story", "movie", "plot"] },
          successtip: "✓ SETTING! Where/when = important for understanding! 🤖🌍"
        },
        {
          type: "mc",
          q: "STORY: 'The main ___ is between the two brothers. One wants peace, the other wants war.' This is the:",
          options: ["conflict", "actor", "genre"],
          a: "conflict",
          successtip: "✓ CONFLICT! The main problem drives the story! ⚔️"
        },
        {
          type: "order",
          q: "Traduce: El giro argumental al final cambió toda la historia.",
          words: ["The", "plot", "twist", "at", "the", "end", "changed", "the", "whole", "story"],
          a: "The plot twist at the end changed the whole story",
          successtip: "✓ Good twist = recontextualizes everything! 🔄🤯"
        },
        {
          type: "mc",
          q: "REAL TALK: 'I loved the characters and the acting, but the plot had too many holes. It didn't make sense.' This means:",
          options: ["Story had logical problems", "Story was perfect", "Actors were bad"],
          a: "Story had logical problems",
          successtip: "✓ Plot holes = things that don't make sense! Ruins immersion! 🕳️"
        }
      ]
    },

    // ===================================================================
    // SET 5: SPOILERS & RECOMMENDATIONS (10 questions) - B1
    // ===================================================================
    {
      id: "m5-u6-s5",
      title: "Set 5: Don't Spoil It!",
      questions: [
        {
          type: "match",
          q: "Match warning vocabulary:",
          pairs: [
            { left: "Spoiler", right: "Reveals plot details" },
            { left: "Spoiler alert", right: "Warning before revealing" },
            { left: "No spoilers!", right: "Don't tell me what happens!" }
          ],
          a: "completed",
          successtip: "✓ Internet etiquette! Protect plot surprises! 🤫"
        },
        {
          type: "mc",
          q: "DIALOGUE: A: 'I just finished Stranger Things!' B: 'No ___! I'm only on episode 3!' A: 'Don't worry, I won't tell!'",
          options: ["spoilers", "movies", "actors"],
          a: "spoilers",
          successtip: "✓ SPOILERS! Protect your friend's experience! 🚫📺"
        },
        {
          type: "input",
          q: "CHAT: 'Spoiler alert! The ending will blow your mind!' What are they about to do? Reveal ___ details.",
          a: "plot",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["story", "ending", "spoiler"] },
          successtip: "✓ Spoiler alert = warning! Gives you time to stop reading! ⚠️"
        },
        {
          type: "order",
          q: "Traduce: No me digas el final, no quiero spoilers.",
          words: ["Don't", "tell", "me", "the", "ending,", "I", "don't", "want", "spoilers"],
          a: "Don't tell me the ending, I don't want spoilers",
          successtip: "✓ Protect your viewing experience! 🙅‍♂️"
        },
        {
          type: "match",
          q: "Recommendation language:",
          pairs: [
            { left: "You should watch...", right: "Strong recommendation" },
            { left: "I recommend...", right: "Polite suggestion" },
            { left: "You might like...", right: "Gentle suggestion" }
          ],
          a: "completed",
          successtip: "✓ Different ways to recommend! 💬"
        },
        {
          type: "mc",
          q: "RECOMMENDATION: 'If you liked Breaking Bad, you ___ watch Better Call Saul. Same universe!'",
          options: ["should", "shouldn't", "could never"],
          a: "should",
          successtip: "✓ SHOULD! Strong recommendation for similar content! 📺✅"
        },
        {
          type: "input",
          q: "DIALOGUE: A: 'What should I watch tonight?' B: 'I ___ The Queen's Gambit. It's about chess but it's amazing!'",
          a: "recommend",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["suggest"] },
          successtip: "✓ RECOMMEND! Sharing favorites with friends! ♟️"
        },
        {
          type: "mc",
          q: "CONTEXT: 'It's about a teacher who becomes a criminal. But I won't say more—no spoilers!' This person is:",
          options: ["Recommending without revealing too much", "Spoiling everything", "Not recommending it"],
          a: "Recommending without revealing too much",
          successtip: "✓ Perfect recommendation! Hook + no spoilers! 🎣🤐"
        },
        {
          type: "order",
          q: "Traduce: Deberías leer ese libro. Es el mejor que he leído este año.",
          words: ["You", "should", "read", "that", "book.", "It's", "the", "best", "I've", "read", "this", "year"],
          a: "You should read that book. It's the best I've read this year",
          successtip: "✓ Personal recommendation + reason = persuasive! 📚⭐"
        },
        {
          type: "mc",
          q: "REAL SCENARIO: Your friend asks 'Is it worth watching?' You say: 'Absolutely! Just avoid trailers—they show too much!' This means:",
          options: ["Watch it, but trailers have spoilers", "Don't watch it", "Trailers are better than the show"],
          a: "Watch it, but trailers have spoilers",
          successtip: "✓ Modern problem! Trailers spoil too much! 🎬🚫"
        }
      ]
    },

    // ===================================================================
    // SET 6: SEQUELS & SERIES (10 questions) - B1
    // ===================================================================
    {
      id: "m5-u6-s6",
      title: "Set 6: More Episodes, Please!",
      questions: [
        {
          type: "match",
          q: "Match continuation vocabulary:",
          pairs: [
            { left: "Sequel", right: "Next movie in series" },
            { left: "Prequel", right: "Story before original" },
            { left: "Season", right: "Set of episodes" }
          ],
          a: "completed",
          successtip: "✓ Sequel ➡️, Prequel ⬅️, Season 📺!"
        },
        {
          type: "mc",
          q: "DIALOGUE: A: 'Did you watch Toy Story 4?' B: 'Yes! I loved it! But the first movie is still my favorite.' Toy Story 4 is a ___.",
          options: ["sequel", "prequel", "remake"],
          a: "sequel",
          successtip: "✓ SEQUEL! Comes after the original! 🎬➡️"
        },
        {
          type: "input",
          q: "CONTEXT: 'Star Wars Episodes 1-3 tell the story of what happened before Episodes 4-6.' Episodes 1-3 are a ___.",
          a: "prequel",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["prequels"] },
          successtip: "✓ PREQUEL! Shows what happened BEFORE! ⬅️⭐"
        },
        {
          type: "order",
          q: "Traduce: Acabo de terminar la segunda temporada. ¿Cuándo sale la tercera?",
          words: ["I", "just", "finished", "the", "second", "season.", "When", "does", "the", "third", "come", "out?"],
          a: "I just finished the second season. When does the third come out?",
          successtip: "✓ Classic question after binge-watching! 📺⏰"
        },
        {
          type: "match",
          q: "Series vocabulary:",
          pairs: [
            { left: "Episode", right: "One part of a series" },
            { left: "Finale", right: "Last episode" },
            { left: "Pilot", right: "First episode" }
          ],
          a: "completed",
          successtip: "✓ Pilot 🚀, Episode 📺, Finale 🎬!"
        },
        {
          type: "mc",
          q: "EXCITEMENT: 'I can't wait for the season ___! I need to know what happens next!' What's the last episode called?",
          options: ["finale", "pilot", "sequel"],
          a: "finale",
          successtip: "✓ FINALE! Last episode = big event! 🎆"
        },
        {
          type: "input",
          q: "DISCUSSION: 'The ___ was slow, but it gets better in episode 3. Keep watching!' (first episode)",
          a: "pilot",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["first episode"] },
          successtip: "✓ PILOT! First episodes establish everything! 🛫"
        },
        {
          type: "mc",
          q: "BINGE-WATCHING: 'I watched all 10 ___ in one day. I couldn't stop!' What did they watch?",
          options: ["episodes", "sequels", "books"],
          a: "episodes",
          successtip: "✓ EPISODES! Binge-watching = watching many episodes at once! 📺🍿"
        },
        {
          type: "order",
          q: "Traduce: La secuela fue decepcionante. La original fue mucho mejor.",
          words: ["The", "sequel", "was", "disappointing.", "The", "original", "was", "much", "better"],
          a: "The sequel was disappointing. The original was much better",
          successtip: "✓ Common opinion! Sequels often disappoint! 📉"
        },
        {
          type: "mc",
          q: "CONTEXT: 'They announced 3 more movies in the franchise! The next one comes out in 2027.' This means:",
          options: ["More sequels are planned", "No more movies", "It's a prequel"],
          a: "More sequels are planned",
          successtip: "✓ Franchise = series of related movies! Marvel/Star Wars style! 🎬🎬🎬"
        }
      ]
    },

    // ===================================================================
    // SET 7: STREAMING & BINGE-WATCHING (10 questions) - B1
    // ===================================================================
    {
      id: "m5-u6-s7",
      title: "Set 7: Modern Viewing Habits",
      questions: [
        {
          type: "match",
          q: "Match streaming vocabulary:",
          pairs: [
            { left: "Binge-watch", right: "Watch many episodes at once" },
            { left: "Stream", right: "Watch online" },
            { left: "Subscription", right: "Monthly payment for service" }
          ],
          a: "completed",
          successtip: "✓ Modern viewing! No more waiting for weekly episodes! 📺"
        },
        {
          type: "mc",
          q: "CONFESSION: 'Last weekend I ___ the entire first season. I couldn't stop watching!'",
          options: ["binge-watched", "streamed", "downloaded"],
          a: "binge-watched",
          successtip: "✓ BINGE-WATCHED! Watching entire season at once = modern habit! 🍿"
        },
        {
          type: "input",
          q: "DIALOGUE: A: 'Where can I watch it?' B: 'It's on Netflix. You can ___ it there.'",
          a: "stream",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["watch"] },
          successtip: "✓ STREAM! Watch online = streaming! 📱💻"
        },
        {
          type: "order",
          q: "Traduce: Anoche vi 8 episodios seguidos. No pude parar.",
          words: ["Last", "night", "I", "watched", "8", "episodes", "in", "a", "row.", "I", "couldn't", "stop"],
          a: "Last night I watched 8 episodes in a row. I couldn't stop",
          successtip: "✓ 'In a row' = seguidos! Classic binge behavior! 📺😅"
        },
        {
          type: "match",
          q: "Streaming platforms:",
          pairs: [
            { left: "Netflix", right: "Red N logo" },
            { left: "Disney+", right: "Mickey ears" },
            { left: "Prime Video", right: "Amazon service" }
          ],
          a: "completed",
          successtip: "✓ Multiple subscriptions = modern life! 💸📺"
        },
        {
          type: "mc",
          q: "BUDGET: 'I pay for 4 different streaming ___ every month. It's getting expensive!'",
          options: ["subscriptions", "movies", "actors"],
          a: "subscriptions",
          successtip: "✓ SUBSCRIPTIONS! Netflix + Disney+ + HBO + Amazon = $$$! 💰"
        },
        {
          type: "input",
          q: "HABIT: 'I always watch shows while eating dinner. It's my daily ___.' (habit/routine)",
          a: "routine",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["habit", "ritual"] },
          successtip: "✓ ROUTINE! Eating + watching = classic combo! 🍽️📺"
        },
        {
          type: "mc",
          q: "COMPLAINT: 'The new episode ___ at midnight, but I have work tomorrow. I'll watch it this weekend.' What happened?",
          options: ["was released/dropped", "disappeared", "was canceled"],
          a: "was released/dropped",
          successtip: "✓ 'Dropped/released' = became available! Modern slang! 🕛"
        },
        {
          type: "order",
          q: "Traduce: Cancelé mi suscripción porque no tenía tiempo para ver nada.",
          words: ["I", "canceled", "my", "subscription", "because", "I", "didn't", "have", "time", "to", "watch", "anything"],
          a: "I canceled my subscription because I didn't have time to watch anything",
          successtip: "✓ Paying for unused subscriptions = waste! 💸"
        },
        {
          type: "mc",
          q: "REALITY: 'I spend more time choosing what to watch than actually watching!' This describes:",
          options: ["Decision paralysis from too many options", "Not having options", "Loving all options"],
          a: "Decision paralysis from too many options",
          successtip: "✓ TOO MANY choices = can't decide! Modern problem! 🤔📺"
        }
      ]
    },

    // ===================================================================
    // SET 8: REVIEWS & RATINGS (10 questions) - B1+
    // ===================================================================
    {
      id: "m5-u6-s8",
      title: "Set 8: Rating Entertainment",
      questions: [
        {
          type: "match",
          q: "Match rating systems:",
          pairs: [
            { left: "5 stars", right: "Perfect/excellent" },
            { left: "3 stars", right: "Average/okay" },
            { left: "1 star", right: "Terrible/awful" }
          ],
          a: "completed",
          successtip: "✓ Star ratings: 5⭐ = best, 1⭐ = worst!"
        },
        {
          type: "mc",
          q: "MINI-REVIEW: 'Amazing visuals, great acting, confusing plot. I give it ___ out of 5 stars.'",
          options: ["3 or 4", "5", "1"],
          a: "3 or 4",
          successtip: "✓ Mixed review = middle rating! Not perfect, not terrible! ⭐⭐⭐"
        },
        {
          type: "input",
          q: "ROTTEN TOMATOES: 'Critics gave it 95% positive reviews. The ___ score is very high!'",
          a: "rating",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["critic", "review"] },
          successtip: "✓ RATING! Rotten Tomatoes = famous review site! 🍅"
        },
        {
          type: "order",
          q: "Traduce: Le doy 5 estrellas. Fue perfecto en todos los sentidos.",
          words: ["I", "give", "it", "5", "stars.", "It", "was", "perfect", "in", "every", "way"],
          a: "I give it 5 stars. It was perfect in every way",
          successtip: "✓ Rare perfect rating! Must be amazing! ⭐⭐⭐⭐⭐"
        },
        {
          type: "match",
          q: "Review vocabulary:",
          pairs: [
            { left: "Overrated", right: "Not as good as people say" },
            { left: "Underrated", right: "Better than people realize" },
            { left: "Overhyped", right: "Too much marketing/buzz" }
          ],
          a: "completed",
          successtip: "✓ Overrated 📉, Underrated 📈, Overhyped 📣!"
        },
        {
          type: "mc",
          q: "OPINION: 'Everyone says it's amazing, but I thought it was just okay. It's totally ___.'",
          options: ["overrated", "underrated", "perfect"],
          a: "overrated",
          successtip: "✓ OVERRATED! When something doesn't live up to hype! 📉"
        },
        {
          type: "input",
          q: "HIDDEN GEM: 'Nobody talks about this show, but it's incredible! It's so ___!'",
          a: "underrated",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ UNDERRATED! Hidden gems = deserve more attention! 💎"
        },
        {
          type: "mc",
          q: "COMPLETE REVIEW: 'The acting was phenomenal. The cinematography was stunning. The soundtrack was perfect. However, the pacing was too slow. Overall: 4/5 stars.' This review is:",
          options: ["Mostly positive with one criticism", "All negative", "All positive"],
          a: "Mostly positive with one criticism",
          successtip: "✓ Balanced review! Praises + one flaw = realistic! 👍👎"
        },
        {
          type: "order",
          q: "Traduce: La mayoría de los críticos lo odiaron, pero a mí me encantó.",
          words: ["Most", "critics", "hated", "it,", "but", "I", "loved", "it"],
          a: "Most critics hated it, but I loved it",
          successtip: "✓ Personal taste ≠ critic opinion! Trust yourself! 🎬❤️"
        },
        {
          type: "mc",
          q: "META-REVIEW: 'It has a 90% on Rotten Tomatoes, but the audience score is only 45%. Critics loved it, but viewers didn't.' This is called:",
          options: ["A divided reception", "Universal praise", "Total failure"],
          a: "A divided reception",
          successtip: "✓ Critics vs audience split = happens often! 🍅📊"
        }
      ]
    },

    // ===================================================================
    // SET 9: DIALOGUE PRACTICE (10 questions) - B1+
    // ===================================================================
    {
      id: "m5-u6-s9",
      title: "Set 9: Talking About What You Watched",
      questions: [
        {
          type: "match",
          q: "Match conversation starters:",
          pairs: [
            { left: "Have you seen...?", right: "Ask if they watched" },
            { left: "What did you think of...?", right: "Ask their opinion" },
            { left: "Did you finish...?", right: "Ask about completion" }
          ],
          a: "completed",
          successtip: "✓ Natural conversation starters! 💬"
        },
        {
          type: "mc",
          q: "DIALOGUE 1: A: 'Have you seen the new Spider-Man?' B: '___' Which response fits?",
          options: ["Yes! It was amazing!", "I'm watching it now", "I'll watch tomorrow"],
          a: "Yes! It was amazing!",
          successtip: "✓ 'Have you seen' = asks about past! Response = past tense! 🕷️"
        },
        {
          type: "input",
          q: "COMPLETE DIALOGUE: A: 'What did you ___ of the ending?' B: 'I loved it! Didn't see it coming!'",
          a: "think",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ 'What did you THINK' = asking opinion! 💭"
        },
        {
          type: "order",
          q: "Ordena el diálogo: A asks about finishing a series",
          words: ["Did", "you", "finish", "the", "series", "yet?"],
          a: "Did you finish the series yet?",
          successtip: "✓ 'Yet' at end = checking on progress! ⏰"
        },
        {
          type: "mc",
          q: "DIALOGUE 2: A: 'I'm on episode 5.' B: 'Oh no spoilers then! But episode 6 is ___!' What fits?",
          options: ["incredible", "last week", "tomorrow"],
          a: "incredible",
          successtip: "✓ No spoilers + hint = building excitement! 🤐✨"
        },
        {
          type: "input",
          q: "RECOMMENDATION: A: 'I'm looking for something to watch.' B: 'You should ___ The Crown. It's historical drama.'",
          a: "watch",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["try", "check out"] },
          successtip: "✓ 'Should watch/try/check out' = recommendations! 👑"
        },
        {
          type: "match",
          q: "Match responses:",
          pairs: [
            { left: "A: 'Was it good?' B: 'Amazing!'", right: "Strong positive" },
            { left: "A: 'Was it good?' B: 'It was okay'", right: "Neutral" },
            { left: "A: 'Was it good?' B: 'Terrible!'", right: "Strong negative" }
          ],
          a: "completed",
          successtip: "✓ Response intensity = opinion strength! 📊"
        },
        {
          type: "mc",
          q: "COMPLETE: A: 'Where can I watch it?' B: '___' Which is most helpful?",
          options: ["It's on Netflix", "It's good", "Yes"],
          a: "It's on Netflix",
          successtip: "✓ Answer the actual question! Platform = helpful info! 📺"
        },
        {
          type: "order",
          q: "Traduce: ¿Vale la pena verla? He escuchado opiniones mixtas.",
          words: ["Is", "it", "worth", "watching?", "I've", "heard", "mixed", "reviews"],
          a: "Is it worth watching? I've heard mixed reviews",
          successtip: "✓ 'Worth watching' = vale la pena! 'Mixed reviews' = opiniones divididas! 🤔"
        },
        {
          type: "mc",
          q: "FULL DIALOGUE: A: 'Have you seen Oppenheimer?' B: 'Yes! The acting was incredible, but it was 3 hours long!' A: 'Worth it?' B: 'Definitely!' What's B's overall opinion?",
          options: ["Positive despite length", "Negative because too long", "Neutral"],
          a: "Positive despite length",
          successtip: "✓ 'Definitely worth it' = overall positive! Length = minor issue! 💣⏰"
        }
      ]
    },

    // ===================================================================
    // SET 10: WRITING MINI-REVIEWS (10 questions) - B1+
    // ===================================================================
    {
      id: "m5-u6-s10",
      title: "Set 10: Your Turn to Review",
      questions: [
        {
          type: "match",
          q: "Match review structure:",
          pairs: [
            { left: "Opening", right: "I watched X last night" },
            { left: "Opinion", right: "It was amazing/terrible" },
            { left: "Details", right: "The acting was great" }
          ],
          a: "completed",
          successtip: "✓ Review structure: What → Opinion → Why! 📝"
        },
        {
          type: "mc",
          q: "START A REVIEW: 'Last weekend I ___ the new Marvel movie. It was incredible!' What verb?",
          options: ["watched", "watch", "watching"],
          a: "watched",
          successtip: "✓ WATCHED! Start with past simple! 🎬"
        },
        {
          type: "input",
          q: "REVIEW BODY: 'The ___ was mind-blowing. I didn't see the twist coming!' (story)",
          a: "plot",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["story", "twist"] },
          successtip: "✓ PLOT! Explain WHAT impressed you! 🤯"
        },
        {
          type: "order",
          q: "Ordena tu review: Talk about acting quality",
          words: ["The", "acting", "was", "phenomenal,", "especially", "the", "lead", "actress"],
          a: "The acting was phenomenal, especially the lead actress",
          successtip: "✓ Specific praise = better review! 🎭⭐"
        },
        {
          type: "mc",
          q: "MIXED REVIEW: 'The visuals were stunning, ___ the dialogue was cheesy.' What connects the ideas?",
          options: ["but", "and", "because"],
          a: "but",
          successtip: "✓ BUT = contrast! Good thing + bad thing! 👍👎"
        },
        {
          type: "input",
          q: "CONCLUSION: 'I give it 4 out of 5 ___. Highly recommend!' (rating unit)",
          a: "stars",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ STARS! End with rating + final thought! ⭐⭐⭐⭐"
        },
        {
          type: "match",
          q: "Review styles:",
          pairs: [
            { left: "Social media", right: "Short, casual, emojis" },
            { left: "Website", right: "Detailed, structured" },
            { left: "Friend chat", right: "Very casual, slang" }
          ],
          a: "completed",
          successtip: "✓ Adjust style to platform! 📱💻💬"
        },
        {
          type: "mc",
          q: "COMPLETE REVIEW: 'I binge-watched it in one day. I ___ stop! The cliffhangers were insane!'",
          options: ["couldn't", "could", "can't"],
          a: "couldn't",
          successtip: "✓ COULDN'T! Past tense + shows how gripping it was! 📺😱"
        },
        {
          type: "order",
          q: "Traduce tu opinión: No es para todos, pero si te gustan los dramas lentos, te encantará.",
          words: ["It's", "not", "for", "everyone,", "but", "if", "you", "like", "slow", "dramas,", "you'll", "love", "it"],
          a: "It's not for everyone, but if you like slow dramas, you'll love it",
          successtip: "✓ Acknowledge preferences! 'Not for everyone' = honest review! 🎭"
        },
        {
          type: "mc",
          q: "FINAL REVIEW CHALLENGE: Which is the BEST complete review?\nA) 'Good movie'\nB) 'I watched Dune last night. The cinematography was breathtaking and the soundtrack was epic. However, the pacing was slow. 4/5 stars. Sci-fi fans will love it!'\nC) 'It was okay I guess'",
          options: ["B - Detailed with reasons", "A - Too short", "C - Too vague"],
          a: "B - Detailed with reasons",
          successtip: "✓ 🎉 PERFECTO! B = complete review! What/Opinion/Details/Rating/Audience! You're a critic now! 🏆✍️"
        }
      ]
    }
  ],

// ===================================================================
// READING SET: My Netflix Obsession
// ===================================================================
readingSet: {
  id: "m5-u6-reading",
  title: "Reading: My Netflix Obsession",
  category: "reading",

  questions: [
    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 1: The Discovery (preguntas 1-3)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**A Confusing Start**

Last month I discovered a show called "Dark." It's a German thriller about time travel. At first, I couldn't understand the plot—it was so confusing! There were too many characters and timelines. I almost quit after episode 2.`
    },
    {
      type: "mc",
      q: "What genre is \"Dark\"?",
      options: ["Thriller", "Comedy", "Romance"],
      a: "Thriller",
      successtip: "✓ THRILLER! Mystery + suspense! 🕵️"
    },
    {
      type: "input",
      q: "What was the initial problem with the show according to the text?",
      a: "plot was confusing",
      accept: { ignoreCase: true, ignorePunctuation: true, alts: ["confusing plot", "too confusing", "didn't understand", "couldn't understand"] },
      successtip: "✓ Complex plot = hard to follow at first! 🤔"
    },
    {
      type: "mc",
      q: "When did the writer almost quit watching the series?",
      options: ["After episode 2", "After season 1", "Never considered quitting"],
      a: "After episode 2",
      successtip: "✓ Episode 2! Almost gave up! 📺"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 2: Getting Hooked (preguntas 4-6)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Total Obsession**

But everyone online said "Keep watching—it gets better!" So I did. By episode 4, I was hooked! The plot twists were incredible. I binge-watched the entire first season in two days. I couldn't stop! The acting was phenomenal and the soundtrack was haunting. By the end of season 1, I was obsessed.`
    },
    {
      type: "mc",
      q: "What changed the writer's mind and kept them watching?",
      options: ["Online recommendations", "It got funnier", "Added more characters"],
      a: "Online recommendations",
      successtip: "✓ Online advice = kept them watching! 💬"
    },
    {
      type: "input",
      q: "How long did it take the person to watch the entire first season?",
      a: "two days",
      accept: { ignoreCase: true, ignorePunctuation: true, alts: ["2 days", "two"] },
      successtip: "✓ TWO DAYS! Classic binge! ⏰📺"
    },
    {
      type: "mc",
      q: "What does the word 'hooked' mean in this context?",
      options: ["Completely engaged/addicted", "Confused", "Bored"],
      a: "Completely engaged/addicted",
      successtip: "✓ HOOKED = addicted! Can't stop watching! 🎣"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 3: Final Verdict (preguntas 7-10)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**The Verdict**

Now I've finished all three seasons. It's the best show I've ever watched! The ending was perfect—no cliffhangers, everything was explained. I give it 5 out of 5 stars. My only complaint? Now I can't find anything else that compares. Everything seems boring after "Dark." I've recommended it to 10 friends. Most said the same thing: confusing at first, but worth it!`
    },
    {
      type: "mc",
      q: "How many seasons are there in total that the writer watched?",
      options: ["Three", "Two", "One"],
      a: "Three",
      successtip: "✓ THREE! Full series! 📺✅"
    },
    {
      type: "input",
      q: "What rating (out of 5) did the writer give the series?",
      a: "5 out of 5",
      accept: { ignoreCase: true, ignorePunctuation: true, alts: ["5/5", "five out of five", "5 stars", "5"] },
      successtip: "✓ Perfect rating! ⭐⭐⭐⭐⭐"
    },
    {
      type: "mc",
      q: "What is the writer's main complaint after finishing the show?",
      options: ["Nothing else compares", "It was too long", "The ending was bad"],
      a: "Nothing else compares",
      successtip: "✓ Post-show depression! Can't find anything as good! 😭"
    },
    {
      type: "mc",
      q: "COMPREHENSION: Which flow best describes the writer's experience?",
      options: ["Initial struggle → Got hooked → Loved it → Recommended it", "Hated it throughout", "Never finished it"],
      a: "Initial struggle → Got hooked → Loved it → Recommended it",
      successtip: "✓ Perfect! Journey from confusion → obsession! Sometimes shows need time! 🌟"
    }
  ]
},
  // ===================================================================
  // REVIEW SET (10 questions)
  // ===================================================================
  reviewSet: {
    id: "m5-u6-review",
    title: "Unit 6 Review: Entertainment Vocabulary",
    questions: [
      {
        type: "match",
        q: "Review genres:",
        pairs: [
          { left: "Comedy", right: "Funny" },
          { left: "Horror", right: "Scary" },
          { left: "Thriller", right: "Suspenseful" }
        ],
        a: "completed",
        successtip: "✓ Main genres reviewed! 😂😱🔍"
      },
      {
        type: "mc",
        q: "The person who performs in movies is called an ___.",
        options: ["actor", "director", "author"],
        a: "actor",
        successtip: "✓ ACTOR! Performs on screen! 🎭"
      },
      {
        type: "input",
        q: "The ___ is the story of the movie.",
        a: "plot",
        accept: { ignoreCase: true, ignorePunctuation: true },
        successtip: "✓ PLOT! What happens! 📖"
      },
      {
        type: "mc",
        q: "'Don't tell me the ending! No ___!'",
        options: ["spoilers", "actors", "genres"],
        a: "spoilers",
        successtip: "✓ SPOILERS! Protect the experience! 🤫"
      },
      {
        type: "order",
        q: "Make a recommendation:",
        words: ["You", "should", "watch", "this", "series"],
        a: "You should watch this series",
        successtip: "✓ Simple recommendation! 📺"
      },
      {
        type: "input",
        q: "The next movie in a series is called a ___.",
        a: "sequel",
        accept: { ignoreCase: true, ignorePunctuation: true },
        successtip: "✓ SEQUEL! Comes after! ➡️"
      },
      {
        type: "mc",
        q: "Watching many episodes at once is called:",
        options: ["binge-watching", "streaming", "acting"],
        a: "binge-watching",
        successtip: "✓ BINGE-WATCHING! Marathon viewing! 📺🍿"
      },
      {
        type: "match",
        q: "Review ratings:",
        pairs: [
          { left: "5 stars", right: "Perfect" },
          { left: "3 stars", right: "Okay" },
          { left: "1 star", right: "Terrible" }
        ],
        a: "completed",
        successtip: "✓ Star system reviewed! ⭐"
      },
      {
        type: "input",
        q: "MINI-REVIEW: 'Last night I ___ a horror movie. It was terrifying!' (verb)",
        a: "watched",
        accept: { ignoreCase: true, ignorePunctuation: true },
        successtip: "✓ WATCHED! Past simple! 😱"
      },
      {
        type: "mc",
        q: "FINAL: 'I watched the new Marvel movie. The action was incredible! The villain was scary. The plot twist shocked me! I give it 4.5/5 stars. You should watch it!' This review includes:",
        options: ["Genre + Opinion + Details + Rating + Recommendation", "Just opinion", "Just rating"],
        a: "Genre + Opinion + Details + Rating + Recommendation",
        successtip: "✓ 🎉 PERFECTO! Complete review structure! You can talk about entertainment like a pro! 🏆🎬"
      }
    ]
  }
};
